<template>
  <benchmark-bar-group
    v-if="loading || props.sector"
    title="Industry Comparison"
    :has-subtitle="!!props.sector"
    :groups="computedGroups"
    :loading="loading"
  >
    <template #subtitle>
      Comparing your metrics against the <strong>{{ props.sector }}</strong> sector average.
    </template>
  </benchmark-bar-group>
</template>

<script setup>
import { computed } from 'vue';
import {
  DELTA_FORMAT,
  INDUSTRY_COMPARISON_GROUPS,
  POLARITY,
  PRODUCT_ENGAGEMENT_GROUP_ID,
} from '@/pages/analyticsBenchmark/benchmarkConfig';
import {
  buildMetricsMap,
  calculateDelta,
  formatValue,
  hasMeaningfulValue,
} from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkBarGroup from '@/pages/analyticsBenchmark/components/BenchmarkBarGroup.vue';

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
  pageVisits: { type: Array, default: () => [] },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

const pageVisitMetrics = computed(() =>
  props.pageVisits.map(visit => {
    const delta = calculateDelta(visit.value, visit.average, {
      deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
      polarity: POLARITY.HIGHER_IS_BETTER,
      unit: 'percent',
    });

    return {
      id: `page-visits-${visit.label.replace(/\s+/g, '-').toLowerCase()}`,
      label: visit.label,
      description: null,
      tooltipId: `ic-page-visits-${visit.label.replace(/\s+/g, '-').toLowerCase()}`,
      value: visit.value,
      average: visit.average,
      maxValue: 100,
      formattedValue: formatValue(visit.value, 'percent'),
      formattedAverage: formatValue(visit.average, 'percent'),
      deltaFormatted: delta.formatted,
      deltaColorClass: delta.colorClass,
    };
  }),
);

const computedGroups = computed(() =>
  INDUSTRY_COMPARISON_GROUPS.map(group => {
    const baseMetrics = group.metrics.map(config => {
      const metric = config.metricId ? metricsMap.value[config.metricId] : null;
      const companyValue = metric?.company_value ?? null;
      const sectorValue = metric?.sector_value ?? null;
      const unit = metric?.unit || 'percent';
      const delta = config.metricId
        ? calculateDelta(companyValue, sectorValue, {
            deltaFormat: config.deltaFormat,
            polarity: config.polarity,
            unit,
          })
        : { raw: null, formatted: 'TBC', colorClass: 'delta-neutral' };

      const isPercentage = unit === 'percent';
      const maxValue = isPercentage ? 100 : Math.max(0, companyValue || 0, sectorValue || 0) * 1.2 || 100;

      return {
        id: config.metricId || config.label,
        label: config.label,
        description: metric?.description || null,
        tooltipId: `ic-${(config.metricId || config.label).replace(/\s+/g, '-')}`,
        value: companyValue,
        average: sectorValue,
        maxValue,
        formattedValue: config.metricId ? formatValue(companyValue, unit) : 'TBC',
        formattedAverage: config.metricId ? formatValue(sectorValue, unit) : 'TBC',
        deltaFormatted: delta.formatted,
        deltaColorClass: delta.colorClass,
      };
    });

    const extras = group.id === PRODUCT_ENGAGEMENT_GROUP_ID ? pageVisitMetrics.value : [];

    return {
      name: group.name,
      metrics: [...baseMetrics, ...extras].filter(m => hasMeaningfulValue(m.value)),
    };
  }).filter(group => group.metrics.length > 0),
);

const hasData = computed(() => !props.loading && computedGroups.value.length > 0);

function getExportTable() {
  if (!hasData.value) {
    return null;
  }
  const rows = [];
  for (const group of computedGroups.value) {
    for (const metric of group.metrics) {
      rows.push([group.name, metric.label, metric.formattedValue, metric.formattedAverage, metric.deltaFormatted]);
    }
  }
  return {
    headers: ['Group', 'Metric', 'You', 'Sector', 'Delta'],
    rows,
  };
}

defineExpose({
  hasData,
  getExportTable,
  getPdfData: () => ({
    benchmarkPanel: {
      title: 'Industry Comparison',
      subtitle: props.sector ? `Compared to the ${props.sector} sector average` : null,
      groups: computedGroups.value,
    },
  }),
});
</script>
